import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import {
  TOTAL,
  MP_DATA,
  CIRCLE_COLORS,
  CIRCLE_HEALTH,
  CIRCLE_HEALTH_TOTAL,
  WATERFALL_STEPS,
  CIRCLE_RETENTION,
  CIRCLE_DATA,
  EXECUTIVE_ACTIONS,
  EXECUTIVE_INSIGHTS,
  EXECUTIVE_NAV_TILES,
  EXECUTIVE_RECOVERY,
} from '../../data/executiveData';
import {
  chartBaseOptions,
  InsightBanner,
  InsightCard,
  KpiCard,
  palette,
  PeriodUnavailableState,
  PriorityBadge,
  SearchField,
  SectionTitle,
  SortButton,
  toneAccent,
} from '../dashboardPrimitives';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

function retentionBadgeClass(retained) {
  if (retained >= 75) return 'status-badge status-badge-green';
  if (retained >= 70) return 'status-badge status-badge-amber';
  return 'status-badge status-badge-red';
}

function statusBadgeClass(rank) {
  if (rank < 4) return 'status-badge status-badge-red';
  if (rank < 9) return 'status-badge status-badge-amber';
  return 'status-badge status-badge-green';
}

function leagueSortValue(row, key) {
  switch (key) {
    case 'rank':
      return row.total;
    case 'circle':
      return row.circle;
    case 'mp':
      return row.mp;
    case 'km':
      return row.km;
    case 'manpower':
      return row.manpower;
    case 'pm':
      return row.pm;
    case 'cm':
      return row.cm;
    case 'intensity':
      return row.intensity;
    default:
      return row.total;
  }
}

function navToneClasses(tone) {
  if (tone === 'red') return { accent: 'text-red-500', iconBg: 'bg-red-50', arrow: 'text-red-500' };
  if (tone === 'orange') return { accent: 'text-amber-600', iconBg: 'bg-amber-50', arrow: 'text-amber-600' };
  return { accent: 'text-blue-600', iconBg: 'bg-blue-50', arrow: 'text-blue-600' };
}

export default function ExecutiveOverview({ onNavigateTab, selectedMonth = "Dec'25" }) {
  const [leagueSort, setLeagueSort] = useState({ key: 'total', dir: 'desc' });
  const [leagueSearch, setLeagueSearch] = useState('');

  const waterfallData = useMemo(() => {
    let runningTotal = TOTAL.gross;
    const stackedFloats = [];
    const colors = [];

    WATERFALL_STEPS.forEach((step) => {
      if (step.anchor) {
        stackedFloats.push([0, step.val]);
        colors.push(step.color);
        return;
      }

      if (step.down) {
        const top = runningTotal;
        runningTotal -= step.val;
        stackedFloats.push([runningTotal, top]);
      } else {
        const bottom = runningTotal;
        runningTotal += step.val;
        stackedFloats.push([bottom, runningTotal]);
      }

      colors.push(step.color);
    });

    return {
      labels: WATERFALL_STEPS.map((step) => step.label),
      datasets: [
        {
          data: stackedFloats,
          backgroundColor: colors,
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  }, []);

  const circles = ['HP', 'KS', 'JA', 'UW'];
  const penaltyCategories = [
    { key: 'manpower', label: 'Manpower Gap', color: palette.danger },
    { key: 'pm_ded', label: 'PM Penalty', color: palette.warning },
    { key: 'cm_pen', label: 'CM Penalty', color: palette.warning },
    { key: 'material', label: 'Material', color: palette.accent },
    { key: 'pq', label: 'Poor Quality', color: palette.slate },
    { key: 'patrol', label: 'Patrolling', color: palette.navy },
    { key: 'flap', label: 'Flap', color: palette.slate },
  ];

  const leagueRows = useMemo(() => {
    return MP_DATA.map((row) => {
      const total = row[3] + row[4] + row[5];
      return {
        circle: row[0],
        mp: row[1],
        km: row[2],
        manpower: row[3],
        pm: row[4],
        cm: row[5],
        avgScore: row[6],
        total,
        intensity: (total / row[2]) * 1000,
      };
    });
  }, []);

  const sortedLeagueRows = useMemo(() => {
    const rows = [...leagueRows];
    const { key, dir } = leagueSort;
    rows.sort((a, b) => {
      const left = leagueSortValue(a, key);
      const right = leagueSortValue(b, key);
      if (typeof left === 'string') {
        return dir === 'asc' ? left.localeCompare(right) : right.localeCompare(left);
      }
      return dir === 'asc' ? left - right : right - left;
    });
    if (!leagueSearch.trim()) return rows;
    const query = leagueSearch.toLowerCase();
    return rows.filter((row) => row.mp.toLowerCase().includes(query) || row.circle.toLowerCase().includes(query));
  }, [leagueRows, leagueSearch, leagueSort]);

  const maxTotal = Math.max(...leagueRows.map((row) => row.total));

  const intensityData = useMemo(() => {
    return leagueRows
      .map((row) => ({
        label: `${row.circle} ${row.mp}`,
        val: row.intensity,
        color: CIRCLE_COLORS[row.circle],
      }))
      .sort((a, b) => b.val - a.val);
  }, [leagueRows]);

  const updateSort = (key) => {
    setLeagueSort((current) => ({
      key,
      dir: current.key === key && current.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  if (selectedMonth === "Feb'26") {
    return <PeriodUnavailableState monthLabel={selectedMonth} />;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
        <KpiCard label="Net Payable" value="₹337.0L" sub="72.9% of ₹462.3L gross retained" dot={palette.success} valueColor={palette.success} progress={72.9} progressColor={palette.success} />
        <KpiCard label="Total Deductions" value="₹125.3L" sub="27.1% of gross lost · 7 penalty categories" dot={palette.danger} valueColor={palette.danger} progress={27.1} progressColor={palette.danger} />
        <KpiCard label="Manpower Gap" value="₹90.5L" sub="68.2% of deductions · #1 driver · 14/14 MPs" dot={palette.danger} valueColor={palette.danger} progress={68.2} progressColor={palette.danger} />
        <KpiCard label="PM Penalty" value="₹19.2L" sub="14.5% of deductions · 6 MPs in 2× repeat loop" dot={palette.warning} valueColor={palette.warning} progress={14.5} progressColor={palette.warning} />
        <KpiCard label="CM Penalty (Net)" value="₹9.96L" sub="Gross ₹17.1L · offset by ₹7.2L FTKM reward" dot={palette.warning} valueColor={palette.warning} progress={7.5} progressColor={palette.warning} />
        <KpiCard label="Other Penalties" value="₹5.7L" sub="Material ₹3.0L · Poor Quality ₹1.3L · Flap ₹0.7L · Patrol ₹0.7L" dot={palette.slate} valueColor={palette.slateDeep} progress={4.5} progressColor={palette.slate} />
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.navySoft}>Payout Waterfall</SectionTitle>
        <p className="dashboard-note">
          Each bar shows the value eroded by that penalty category. Manpower Gap alone accounts for ₹90.5L, or 72% of all value lost.
        </p>
        <div style={{ height: 228 }}>
          <Bar
            data={waterfallData}
            options={{
              ...chartBaseOptions,
              scales: {
                x: {
                  grid: { display: false },
                  border: { display: false },
                  ticks: {
                    color: palette.slateDeep,
                    font: { family: 'Inter', size: 10, weight: '500' },
                  },
                },
                y: {
                  min: 0,
                  max: 490,
                  grid: { color: palette.border },
                  border: { display: false },
                  ticks: {
                    callback: (value) => `₹${value.toFixed(0)}L`,
                    color: palette.slateDeep,
                    font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                  },
                },
              },
              plugins: {
                ...chartBaseOptions.plugins,
                tooltip: {
                  displayColors: false,
                  callbacks: {
                    label: (context) => {
                      const delta = context.raw[1] - context.raw[0];
                      return `₹${Math.abs(delta).toFixed(1)}L`;
                    },
                  },
                },
              },
            }}
          />
        </div>

        <div className="mt-5 border-t border-app-border pt-4">
          <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
            Circle Penalty Retention
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            {CIRCLE_RETENTION.map((circle) => (
              <div key={circle.circle} className="rounded-xl border border-app-border bg-slate-50/80 p-3.5">
                <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  <span className="h-2 w-2 rounded-full" style={{ background: circle.color }} />
                  {circle.circle} Circle
                </div>
                <div className="data-value text-base font-semibold text-navy">{circle.pct}%</div>
                <div className="mt-1 text-[11px] text-slate-500">₹{circle.net}L of ₹{circle.gross}L retained</div>
                <div className="mt-3 h-1.5 rounded-full bg-slate-200">
                  <div className="h-full rounded-full" style={{ width: `${circle.pct}%`, background: circle.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Circle Penalty Breakdown</SectionTitle>
          <p className="dashboard-note">Stacked by category. FTKM reward is shown as a recovery offset rather than decorative gain.</p>
          <div style={{ height: 272 }}>
            <Bar
              data={{
                labels: circles.map((circle) => `${circle} Circle`),
                datasets: [
                  ...penaltyCategories.map((category) => ({
                    label: category.label,
                    data: circles.map((circle) => CIRCLE_DATA[circle][category.key] || 0),
                    backgroundColor: category.color,
                    borderRadius: 4,
                    borderSkipped: false,
                  })),
                  {
                    label: 'FTKM Reward',
                    data: circles.map((circle) => -(CIRCLE_DATA[circle].ftkm_rwd || 0)),
                    backgroundColor: palette.success,
                    borderRadius: 4,
                    borderSkipped: false,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      color: palette.slateDeep,
                      font: { family: 'Inter', size: 10, weight: '500' },
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    grid: { display: false },
                    border: { display: false },
                    ticks: {
                      color: palette.slateDeep,
                      font: { family: 'Inter', size: 10, weight: '500' },
                    },
                  },
                  y: {
                    stacked: true,
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `₹${value.toFixed(0)}L`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Penalty Intensity</SectionTitle>
          <p className="dashboard-note">Normalised to ₹ lakh exposure per 1,000 KM for a clean MP-to-MP comparison.</p>
          <div style={{ height: 272 }}>
            <Bar
              data={{
                labels: intensityData.map((row) => row.label),
                datasets: [
                  {
                    data: intensityData.map((row) => row.val),
                    backgroundColor: intensityData.map((row) => row.color),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                indexAxis: 'y',
                scales: {
                  x: {
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `₹${value.toFixed(1)}L`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                  y: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: {
                      color: palette.slateDeep,
                      font: { family: 'Inter', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.accent}>Circle Health Scorecard</SectionTitle>
        <p className="dashboard-note">Full penalty breakdown per circle with KPI health indicators. Drill into MPs via the dashboards below.</p>
        <div className="table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>Circle</th>
                <th>Scope KM</th>
                <th>Gross Pay</th>
                <th>Net Pay</th>
                <th>% Retained</th>
                <th>Manpower Gap</th>
                <th>PM Penalty</th>
                <th>CM Penalty</th>
                <th>FTKM Reward</th>
                <th>Avg CM Score</th>
                <th>Penalty / 1000 KM</th>
              </tr>
            </thead>
            <tbody>
              {CIRCLE_HEALTH.map((circle) => (
                <tr key={circle.circle}>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: circle.color }} />
                      <div>
                        <div className="mono-cell font-semibold text-slate-700">{circle.circle}</div>
                        <div className="text-[10px] text-slate-400">{circle.subtext}</div>
                      </div>
                    </div>
                  </td>
                  <td className="mono-cell">{circle.km.toLocaleString()}</td>
                  <td className="mono-cell">₹{circle.gross}L</td>
                  <td className="mono-cell">₹{circle.net}L</td>
                  <td>
                    <span className={retentionBadgeClass(circle.retained)}>{circle.retained}%</span>
                  </td>
                  <td className="mono-cell text-red-500">₹{circle.manpower}L</td>
                  <td className="mono-cell text-amber-600">₹{circle.pm}L</td>
                  <td className="mono-cell text-amber-600">₹{circle.cm}L</td>
                  <td className="mono-cell text-emerald-600">{circle.ftkm > 0 ? `+₹${circle.ftkm}L` : '₹0L'}</td>
                  <td className="mono-cell">{circle.avgCm}</td>
                  <td className="mono-cell">₹{circle.penPerKm}L</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td>Zone Total</td>
                <td className="mono-cell">{CIRCLE_HEALTH_TOTAL.km.toLocaleString()}</td>
                <td className="mono-cell">₹{CIRCLE_HEALTH_TOTAL.gross}L</td>
                <td className="mono-cell">₹{CIRCLE_HEALTH_TOTAL.net}L</td>
                <td className="mono-cell">{CIRCLE_HEALTH_TOTAL.retained}%</td>
                <td className="mono-cell text-red-500">₹{CIRCLE_HEALTH_TOTAL.manpower}L</td>
                <td className="mono-cell text-amber-600">₹{CIRCLE_HEALTH_TOTAL.pm}L</td>
                <td className="mono-cell text-amber-600">₹{CIRCLE_HEALTH_TOTAL.cm}L</td>
                <td className="mono-cell text-emerald-600">+₹{CIRCLE_HEALTH_TOTAL.ftkm}L</td>
                <td className="mono-cell">{CIRCLE_HEALTH_TOTAL.avgCm}</td>
                <td className="mono-cell">₹{CIRCLE_HEALTH_TOTAL.penPerKm}L</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.accent}>MP Penalty League Table</SectionTitle>
        <p className="dashboard-note">Combined Manpower Gap + PM Penalty + CM Penalty per MP, ranked by total exposure.</p>
        <div className="mb-4 flex justify-end">
          <SearchField value={leagueSearch} onChange={setLeagueSearch} placeholder="Search MP..." className="sm:max-w-xs" />
        </div>
        <div className="table-scroll-shell">
          <table className="evidence-table">
            <thead>
              <tr>
                <th><SortButton label="Rank" sortKey="rank" sortState={leagueSort} onSort={updateSort} /></th>
                <th><SortButton label="Circle" sortKey="circle" sortState={leagueSort} onSort={updateSort} /></th>
                <th><SortButton label="MP" sortKey="mp" sortState={leagueSort} onSort={updateSort} /></th>
                <th><SortButton label="Scope KM" sortKey="km" sortState={leagueSort} onSort={updateSort} /></th>
                <th><SortButton label="Manpower Gap" sortKey="manpower" sortState={leagueSort} onSort={updateSort} /></th>
                <th><SortButton label="PM Penalty" sortKey="pm" sortState={leagueSort} onSort={updateSort} /></th>
                <th><SortButton label="CM Penalty" sortKey="cm" sortState={leagueSort} onSort={updateSort} /></th>
                <th><SortButton label="Total Penalty" sortKey="total" sortState={leagueSort} onSort={updateSort} /></th>
                <th><SortButton label="₹ / 1,000 KM" sortKey="intensity" sortState={leagueSort} onSort={updateSort} /></th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedLeagueRows.length ? sortedLeagueRows.map((row, index) => {
                const pct = (row.total / maxTotal) * 100;
                const intensityColor =
                  row.intensity > 4 ? 'text-red-500' : row.intensity > 2.5 ? 'text-amber-600' : 'text-emerald-600';

                return (
                  <tr key={row.mp}>
                    <td>
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600">
                        {index + 1}
                      </span>
                    </td>
                    <td>
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: CIRCLE_COLORS[row.circle] }} />
                        <span className="mono-cell font-semibold text-slate-700">{row.circle}</span>
                      </span>
                    </td>
                    <td className="mono-cell font-semibold text-slate-700">{row.mp}</td>
                    <td className="mono-cell">{row.km.toLocaleString()}</td>
                    <td className="mono-cell text-red-500">₹{row.manpower.toFixed(2)}L</td>
                    <td className="mono-cell text-amber-600">₹{row.pm.toFixed(2)}L</td>
                    <td className="mono-cell text-amber-600">
                      ₹{row.cm.toFixed(2)}L
                      <div className="mt-1 text-[10px] text-slate-400">Avg score <span className="mono-cell">{row.avgScore}</span></div>
                    </td>
                    <td>
                      <div className="data-value text-sm font-semibold text-slate-800">₹{row.total.toFixed(2)}L</div>
                      <div className="mt-1 h-1.5 w-24 rounded-full bg-slate-100">
                        <div className="h-full rounded-full bg-navy" style={{ width: `${pct}%` }} />
                      </div>
                    </td>
                    <td className={`mono-cell font-semibold ${intensityColor}`}>₹{row.intensity.toFixed(2)}L</td>
                    <td>
                      <span className={statusBadgeClass(index)}>{index < 4 ? 'Critical' : index < 9 ? 'Elevated' : 'Managed'}</span>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="10" className="py-10 text-center text-slate-400">No data for selected filters</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <SectionTitle dot={palette.slateDeep}>Deep-Dive Dashboards</SectionTitle>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {EXECUTIVE_NAV_TILES.map((tile) => {
            const tone = navToneClasses(tile.tone);
            return (
              <button
                key={tile.title}
                type="button"
                onClick={() => onNavigateTab?.(tile.tabIndex)}
                className="nav-tile-card text-left"
              >
                <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl text-lg ${tone.iconBg}`}>
                  {tile.icon}
                </div>
                <div className={`text-sm font-bold ${tone.accent}`}>{tile.title}</div>
                <div className={`data-value mt-2 text-[1.25rem] font-bold ${tone.accent}`}>{tile.penalty}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {tile.stats.map((stat) => (
                    <span key={stat} className="summary-chip bg-slate-50 text-slate-600">
                      {stat}
                    </span>
                  ))}
                </div>
                <div className={`mt-4 text-[11px] font-semibold uppercase tracking-[0.06em] ${tone.arrow}`}>{tile.arrow}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <InsightBanner />
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          {EXECUTIVE_INSIGHTS.map((insight) => (
            <InsightCard key={insight.title} {...insight} />
          ))}
        </div>
      </div>

      <div className="recovery-hero">
        <div className="recovery-hero-head px-6 py-6 sm:px-8">
          <div className="grid gap-6 lg:grid-cols-[1.5fr,1fr] lg:items-center">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/55">{EXECUTIVE_RECOVERY.heading}</div>
              <div className="mt-2 text-[1.4rem] font-bold leading-tight text-white">{EXECUTIVE_RECOVERY.title}</div>
              <div className="mt-2 max-w-3xl text-sm leading-relaxed text-white/65">{EXECUTIVE_RECOVERY.body}</div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {EXECUTIVE_RECOVERY.headlineStats.map((stat) => {
                const accent = toneAccent(stat.tone);
                return (
                  <div key={stat.label} className="rounded-xl border border-white/10 bg-white/5 px-4 py-4 text-center">
                    <div className="text-[1.45rem] font-bold" style={{ color: accent.line }}>{stat.value}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-[0.07em] text-white/50">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 border-b border-app-border md:grid-cols-2 xl:grid-cols-4">
          {EXECUTIVE_RECOVERY.stripStats.map((stat) => {
            const accent = toneAccent(stat.tone);
            return (
              <div key={stat.label} className="border-r border-app-border px-5 py-4 last:border-r-0">
                <div className="text-xl font-bold" style={{ color: accent.line }}>{stat.value}</div>
                <div className="mt-1 text-[11px] font-semibold text-slate-500">{stat.annual}</div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.06em] text-slate-400">
                  {stat.label}
                  <br />
                  {stat.sublabel}
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 divide-y divide-app-border lg:grid-cols-2 lg:divide-x lg:divide-y-0">
          {EXECUTIVE_RECOVERY.items.map((item) => {
            const accent = toneAccent(item.tone);
            return (
              <div key={item.title} className="recovery-item" style={{ borderLeft: `4px solid ${accent.line}` }}>
                <div
                  className="inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.06em]"
                  style={{ backgroundColor: accent.soft, color: accent.text }}
                >
                  {item.category}
                </div>
                <div className="mt-3 text-sm font-semibold text-slate-900">{item.title}</div>
                <div className="mt-2 text-[1.65rem] font-bold" style={{ color: accent.line }}>{item.value}</div>
                <div className="mt-1 text-[11px] font-semibold text-slate-500">{item.annual}</div>
                <p className="mt-3 text-[12px] leading-6 text-slate-600">{item.reason}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.accent}>Prioritised Action Plan</SectionTitle>
        <div className="table-scroll-shell">
          <table className="action-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Action</th>
                <th>Category</th>
                <th>Est. Impact</th>
                <th>Owner</th>
              </tr>
            </thead>
            <tbody>
              {EXECUTIVE_ACTIONS.map((row) => (
                <tr key={row.action}>
                  <td><PriorityBadge priority={row.priority} /></td>
                  <td className="text-[12px] font-medium text-slate-700">{row.action}</td>
                  <td>{row.category}</td>
                  <td className="mono-cell font-semibold text-emerald-600">{row.saving}</td>
                  <td>{row.owner}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
